import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Input, Select } from 'antd';
import { FilterBar } from '../../../components/list';
import type { HostTenantStatus } from '../../tenants/api/tenants';
import type { LicenseFilter } from '../utils/tenantStatus';
import { useDebounce } from '../../../hooks/useDebounce';

export interface HostTenantFilterValues {
  search?: string;
  status?: HostTenantStatus;
  license: LicenseFilter;
}

interface HostTenantFilterBarProps {
  value: HostTenantFilterValues;
  onChange: (value: HostTenantFilterValues) => void;
  onReset: () => void;
}

const STATUS_OPTIONS: { value: HostTenantStatus; label: string }[] = [
  { value: 'Requested', label: 'Requested' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Active', label: 'Active' },
  { value: 'Expired', label: 'Expired' },
  { value: 'Rejected', label: 'Rejected' },
];

const LICENSE_OPTIONS: { value: LicenseFilter; label: string }[] = [
  { value: 'all', label: 'All licenses' },
  { value: 'expiring', label: 'Expiring soon' },
  { value: 'overdue', label: 'License overdue' },
  { value: 'none', label: 'No license date' },
];

const HostTenantFilterBar: React.FC<HostTenantFilterBarProps> = ({ value, onChange, onReset }) => {
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  useLayoutEffect(() => { valueRef.current = value; }, [value]);
  useLayoutEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  const lastCommittedRef = useRef(value.search ?? '');

  const [localSearch, setLocalSearch] = useState(value.search ?? '');
  const debouncedSearch = useDebounce(localSearch, 300);

  // Sync debounced value to parent (passive debounce — fires ~300ms after typing stops)
  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (trimmed === lastCommittedRef.current) return;
    lastCommittedRef.current = trimmed;
    onChangeRef.current({ ...valueRef.current, search: trimmed || undefined });
  }, [debouncedSearch]);

  // Sync from parent (e.g., external reset)
  useEffect(() => {
    setLocalSearch(value.search ?? '');
  }, [value.search]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalSearch(val);
    if (val === '') {
      // Clear (X) button — fire immediately
      if (lastCommittedRef.current === '') return;
      lastCommittedRef.current = '';
      onChangeRef.current({ ...valueRef.current, search: undefined });
    }
  };

  const handleSearch = (v: string) => {
    // Enter key or search button — fire immediately (no debounce)
    const trimmed = v.trim();
    setLocalSearch(v);
    if (trimmed === lastCommittedRef.current) return;
    lastCommittedRef.current = trimmed;
    onChangeRef.current({ ...valueRef.current, search: trimmed || undefined });
  };

  const hasActiveFilters =
    Boolean(value.search) || value.status !== undefined || value.license !== 'all';

  return (
    <FilterBar onReset={hasActiveFilters ? onReset : undefined}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Search</span>
        <Input.Search
          allowClear
          style={{ width: 280 }}
          value={localSearch}
          onChange={handleSearchChange}
          onSearch={handleSearch}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Status</span>
        <Select<HostTenantStatus | undefined>
          allowClear
          style={{ width: 160 }}
          value={value.status}
          onChange={(status) => onChange({ ...value, status })}
          options={STATUS_OPTIONS}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>License</span>
        <Select<LicenseFilter>
          style={{ width: 180 }}
          value={value.license}
          onChange={(license) => onChange({ ...value, license })}
          options={LICENSE_OPTIONS}
        />
      </div>
    </FilterBar>
  );
};

export default HostTenantFilterBar;
